import React, { Component } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import {
  apiErrorMessage,
  firstError,
  hasErrors,
  validateBrand,
} from "Helpers/ContentValidation";
import { brandFormToFormData, brandToFormValues, emptyBrandForm } from "Helpers/ContentPayloads";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Grid } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import BrandFormFields from "components/ContentForms/BrandFormFields";

export default class UpdateBrand extends Component {
  state = {
    id: "",
    values: emptyBrandForm(),
    existingLogo: "",
    errors: {},
    parentOptions: [],

    fetching: true,
    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    const id = window.location.pathname.split("/")[2];

    if (!id) {
      this.setState({ fetching: false });
      return this.notify("warning", "No brand selected");
    }

    this.setState({ id }, () => {
      this.fetchBrand(id);
      this.fetchParentOptions(id);
    });
  }

  /* =============================
     LOAD
  ============================= */
  fetchBrand = async (id) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.get(Constants.getUrls.getBrand + id);
      const brand = response.data?.data;

      if (!brand) throw new Error("Brand not found");

      this.setState({
        values: brandToFormValues(brand),
        existingLogo: brand.logo || "",
        fetching: false,
      });
    } catch (error) {
      this.setState({ fetching: false });
      this.notify("warning", apiErrorMessage(error, "Could not load the brand"));
    }
  };

  fetchParentOptions = async (id) => {
    try {
      const response = await axios.get(Constants.getUrls.getBrandOptions);

      // A brand cannot be its own parent, and only top-level brands can be one.
      const options = (response.data?.data || []).filter(
        (option) => !option.parent_brand && option._id !== id
      );
      this.setState({ parentOptions: options });
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not load brands"));
    }
  };

  /* =============================
     FORM STATE
  ============================= */
  handleChange = (name, value) => {
    this.setState((state) => {
      const errors = { ...state.errors };
      delete errors[name];
      return { values: { ...state.values, [name]: value }, errors };
    });
  };

  handleFile = (file) => {
    this.setState((state) => ({
      values: { ...state.values, logo_file: file },
      errors: { ...state.errors, logo: undefined },
    }));
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  /* =============================
     SUBMIT
  ============================= */
  handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateBrand(this.state.values, {
      isUpdate: true,
      hasLogo: Boolean(this.state.existingLogo),
    });

    if (hasErrors(errors)) {
      this.setState({ errors });
      return this.notify("warning", firstError(errors));
    }

    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true, errors: {} });

    try {
      const response = await axios.put(
        Constants.putUrls.updateBrand + this.state.id,
        brandFormToFormData(this.state.values)
      );

      this.setState({
        loading: false,
        existingLogo: response.data?.data?.logo || this.state.existingLogo,
        values: { ...this.state.values, logo_file: null },
      });

      this.notify("success", response.data?.message || "Brand updated successfully!");
      setTimeout(() => this.setState({ redirect: true }), 1200);
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not update the brand"));
    }
  };

  render() {
    if (this.state.redirect) return <Navigate to="/brandpages" replace />;

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Update Brand Page
              </MDTypography>
            </MDBox>

            <MDBox py={3} px={2}>
              {this.state.fetching ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : (
                <form onSubmit={this.handleSubmit} noValidate>
                  <BrandFormFields
                    values={this.state.values}
                    errors={this.state.errors}
                    onChange={this.handleChange}
                    onFile={this.handleFile}
                    parentOptions={this.state.parentOptions}
                    existingLogo={this.state.existingLogo}
                    isUpdate
                  />

                  <MDBox mt={4} display="flex" gap={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      disabled={this.state.loading}
                    >
                      {this.state.loading ? "Updating..." : "Update Brand"}
                    </MDButton>

                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={() => this.setState({ redirect: true })}
                      disabled={this.state.loading}
                    >
                      Cancel
                    </MDButton>
                  </MDBox>
                </form>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Brand Pages"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
